/**
 * Choosing how long a note should last (NIP-40).
 *
 * Its own component for the same reason the mute confirmation is: the *wording* is
 * the load-bearing part, and it does not survive being squeezed into the composer's
 * button row as a tooltip.
 *
 * What has to be said, and why the control would be harmful without it: an
 * expiration is a request, not a deletion and not a guarantee. Relays that
 * implement NIP-40 stop serving the event when it lapses; relays that do not keep
 * serving it forever, and every client that already fetched it keeps its copy. A
 * reader who believes "expires in 1 hour" means "gone in 1 hour" will use it to post
 * things they would otherwise not post, which makes an unlabelled version of this
 * control worse than not shipping one at all.
 *
 * So the menu names the honest version of the promise, and the line under the
 * composer repeats it once a choice is made — where it cannot be missed, rather than
 * in a tooltip a touch screen never shows.
 */

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@setu/ui";
import { Timer } from "lucide-react";

/** One offered lifetime. `undefined` seconds is the "no expiry" entry. */
interface ExpiryOption {
  readonly label: string;
  readonly seconds?: number;
}

const HOUR = 3600;

/*
 * Deliberately few and deliberately coarse.
 *
 * A minute-level picker would imply a precision the mechanism does not have: relays
 * sweep on their own schedule, so the real lapse is "some time after this", and
 * offering 07:43 invites the reader to believe otherwise.
 */
const OPTIONS: readonly ExpiryOption[] = [
  { label: "No expiry" },
  { label: "1 hour", seconds: HOUR },
  { label: "6 hours", seconds: 6 * HOUR },
  { label: "1 day", seconds: 24 * HOUR },
  { label: "1 week", seconds: 7 * 24 * HOUR },
];

export interface ExpirySelectProps {
  /** Seconds from now, or undefined for a note with no expiry. */
  value?: number;
  onChange(seconds: number | undefined): void;
  disabled?: boolean;
}

/** The chosen lifetime's label, for the notice under the composer. */
export function expiryLabel(seconds: number | undefined): string | undefined {
  if (seconds === undefined) return undefined;
  return OPTIONS.find((option) => option.seconds === seconds)?.label;
}

export function ExpirySelect({ value, onChange, disabled }: ExpirySelectProps) {
  const active = value !== undefined;
  return (
    <DropdownMenu>
      <Tooltip label="Set an expiry">
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={
              active
                ? `Expires in ${expiryLabel(value)} — change`
                : "Set an expiry"
            }
            aria-pressed={active}
            disabled={disabled}
            className={
              active
                ? "text-warning"
                : "text-muted-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground"
            }
          >
            <Timer />
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent align="start" className="max-w-xs">
        {OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.label}
            onSelect={() => onChange(option.seconds)}
            // The current choice is marked rather than removed from the list, so
            // the menu does not reflow between openings.
            aria-current={option.seconds === value ? "true" : undefined}
          >
            <span className="flex-1">{option.label}</span>
            {option.seconds === value ? (
              <span aria-hidden className="text-muted-foreground">
                ✓
              </span>
            ) : null}
          </DropdownMenuItem>
        ))}
        {/* Not a tooltip and not optional. This is the only place the reader is
            told what the feature actually does. */}
        <p className="px-2 py-1.5 text-2xs text-muted-foreground">
          A request, not a guarantee. Relays that support expiry stop serving
          the note; others keep it, and anyone who already read it keeps their
          copy.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
